import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';

// Components
import Sidebar from './Sidebar';
import StepComponent from './StepComponent';
import SweetAlertService from '../../../common/SweetAlert/SweetAlert';
import ExpiredTokenPage from '../ExpiredTokenPage'; // ✅ Imported Expired Page
import LoginFormFORprospect from '../LoginFormFORprospect';
import { submitSessionUrl } from '../../../redux/SessionUrl/sessionUrlSlice';

// Redux Actions
import {
  fetchNcodes,
  fetchResult,
  checkTokenExpiry // ✅ Imported Action
} from '../../../redux/tool/toolSlice';

// Redux Selectors
import {
  selectNcodesData,
  selectNcodesLoading,
  selectNcodesError,
  selectResultData,
  selectResultLoading,
  selectResultError,
  // ✅ Imported Token Selectors
  selectTokenExpiryCheckData,
  selectTokenExpiryCheckLoading
} from '../../../redux/tool/toolSelectors';
import { selectSubmitSessionUrlData } from '../../../redux/SessionUrl/sessionUrlSelectors';
import ThreeDotBouncingLoader from '../../../common/element/ButtonLoader/ThreeDotBouncingLoder';
import InvalidTokenPage from '../InvalidToken';

const Ncode = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  // Clear prospect token from localStorage on mount (handles refresh)
  useEffect(() => {
    try {
      localStorage.removeItem('prospect_token');
    } catch (e) { }
  }, []);

  // ✅ Get token from URL
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  // ✅ Get prospect company params from URL
  const companyIdParam = searchParams.get('company_id');
  const companyNameParam = searchParams.get('company_name');
  const companySessionIdParam = searchParams.get('company_session_id');
  const sessionNameParam = searchParams.get('session_name');
  const sessionTypeParam = searchParams.get('session_type');
  const isSessionUrlFlow = sessionTypeParam === 'session_url';

  // Whether prospect login should be shown before main flow
  const [prospectRequired] = useState(Boolean(companyIdParam && companyNameParam));
  const [prospectConfirmed, setProspectConfirmed] = useState(false);
  // Try to read prospect token from localStorage if present
  const storedProspectToken = (() => { try { return localStorage.getItem('prospect_token'); } catch (e) { return null; } })();
  const [prospectToken, setProspectToken] = useState(storedProspectToken || token || null);
  const [sessionUrlUserData, setSessionUrlUserData] = useState(null);

  // Existing Selectors
  const ncodesData = useSelector(selectNcodesData);
  const ncodesLoading = useSelector(selectNcodesLoading);
  const ncodesError = useSelector(selectNcodesError);

  const resultData = useSelector(selectResultData);
  const resultLoading = useSelector(selectResultLoading);
  const resultError = useSelector(selectResultError);

  // ✅ Token Expiry Selectors
  const tokenExpiryData = useSelector(selectTokenExpiryCheckData);
  const tokenExpiryLoading = useSelector(selectTokenExpiryCheckLoading);

  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [stepSelections, setStepSelections] = useState({
    1: [],
    2: [],
    3: [],
    4: [],
    5: []
  });

  const stepConfigs = {
    1: {
      title: 'Step 1',
      instruction: 'From below list, select as many N Codes as you want, that you can relate to the most. Select minimum 24 N Codes.',
      requiredCount: 24
    },
    2: {
      title: 'Step 2',
      instruction: 'Now from the below N Codes you have selected, select 12 N Codes that you can relate to the most.',
      requiredCount: 12
    },
    3: {
      title: 'Step 3',
      instruction: 'Now from the below N Codes you have selected, select 6 N Codes that you can relate to the most.',
      requiredCount: 6
    },
    4: {
      title: 'Step 4',
      instruction:
        id === 'ncodes'
          ? 'Your 6 N - Codes are:'
          : id === 'ncodes-2'
            ? 'Your 6 N Codes About Others Are:'
            : id === 'ncodes-3'
              ? 'Your 6 Sales N Codes Are:'
              : 'These are your selected 6 N Codes from step 3. Please review them before proceeding.',
      requiredCount: 6,
      isReadOnly: true
    },
    5: {
      title: 'Step 5',
      instruction: 'The E Codes Against Your 6 N Codes Are:',
      requiredCount: 0,
      isReadOnly: true,
      isResultStep: true
    }
  };

  // ✅ 1. Check Token Expiry on Mount
  useEffect(() => {
    // Do not call expiry check for prospect flows (prospectRequired)
    if (prospectRequired) return;

    const effective = prospectToken || token;
    if (effective) {
      dispatch(checkTokenExpiry(effective));
    }
  }, [dispatch, token, prospectToken, prospectRequired]);

  // Fetch NCodes
  useEffect(() => {
    // If prospect login is required, wait until it's confirmed before fetching NCodes
    if (id && (!prospectRequired || prospectConfirmed)) {
      dispatch(fetchNcodes(id));
    }
  }, [dispatch, id, prospectRequired, prospectConfirmed]);

  useEffect(() => {
    if (currentStep === 4 && stepSelections[3].length === 6) {
      setStepSelections(prev => ({
        ...prev,
        4: [...stepSelections[3]]
      }));
    }
  }, [currentStep, stepSelections[3]]);

  const masterNCodes = React.useMemo(() => {
    if (!ncodesData || !Array.isArray(ncodesData)) return [];
    return ncodesData.map(item => ({
      id: item.id,
      text: item.ncode
    }));
  }, [ncodesData]);

  const submitSessionUrlData = useSelector(selectSubmitSessionUrlData);

  const resultCCodes = React.useMemo(() => {
    if (!resultData || !Array.isArray(resultData)) return [];
    return resultData;
  }, [resultData]);

  const displayResultCodes = isSessionUrlFlow
    ? (Array.isArray(submitSessionUrlData) ? submitSessionUrlData : [])
    : resultCCodes;

  const handleSelectionChange = (newSelection) => {
    if (currentStep === 4 || currentStep === 5) {
      return;
    }

    setStepSelections(prev => {
      const updated = {
        ...prev,
        [currentStep]: newSelection
      };

      for (let s = currentStep + 1; s <= 5; s++) {
        if (updated[s] && updated[s].length > 0) {
          updated[s] = [];
        }
      }

      return updated;
    });
  };

  const handleNext = async () => {
    if (!completedSteps.includes(currentStep)) {
      setCompletedSteps(prev => [...prev, currentStep]);
    }

    if (currentStep === 4) {
      const selectedIds = stepSelections[4].map(item => item.id);

      if (isSessionUrlFlow) {
        const resultAction = await dispatch(
          submitSessionUrl({
            company_session_id: Number(companySessionIdParam),
            name: sessionUrlUserData?.name || "",
            email: sessionUrlUserData?.email || "",
            phone: sessionUrlUserData?.phone || "",
            result_json: { id, ids: selectedIds },
          })
        );
        if (submitSessionUrl.rejected.match(resultAction)) {
          SweetAlertService.showError({
            title: "Error!",
            message: resultAction.payload || "Something went wrong. Please try again.",
          });
          return;
        }
      } else {
        // ✅ Pass both ids and token to fetchResult
        // Prefer in-memory prospectToken, fallback to URL token, then localStorage (in case state hasn't updated yet)
        const stored = (() => { try { return localStorage.getItem('prospect_token'); } catch (e) { return null; } })();
        const effectiveToken = prospectToken || token || stored || null;
        // Ensure token expiry is checked before fetching results so backend knows token status.
        // Skip this check for prospect flows where we intentionally avoid hitting expiry endpoint.
        if (effectiveToken && !prospectRequired) {
          dispatch(checkTokenExpiry(effectiveToken));
        }
        dispatch(fetchResult({ id: id, ids: selectedIds, token: effectiveToken }));
      }
    }

    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    } else {
      console.log('Process completed!');

      SweetAlertService.showCongratulations({
        onGoHome: () => {
          setCurrentStep(1);
          setCompletedSteps([]);
          setStepSelections({ 1: [], 2: [], 3: [], 4: [], 5: [] });

          if (isSessionUrlFlow) {
            setProspectConfirmed(false);
            setSessionUrlUserData(null);
            return;
          }

          // Decide destination: if prospect params exist, send user back to prospect page
          if (companyIdParam) {
            navigate(`/prospectCompany/view?id=${companyIdParam}`);
          } else {
            // Preserve token in navigation if needed
            const effective = prospectToken || token;
            // Only check token expiry for non-prospect flows
            if (effective && !prospectRequired) dispatch(checkTokenExpiry(effective));
            const tokenParam = effective ? `?token=${effective}` : '';
            navigate(`/tool/${id}${tokenParam}`);
          }
        }
      });
    }
  };

  const handleCancel = () => {
    if (currentStep > 1) {
      const newStep = currentStep - 1;
      setCurrentStep(newStep);
      setCompletedSteps(prev => prev.filter(s => s <= newStep));
      return;
    }

    SweetAlertService.showConfirmation({
      title: 'Are you sure?',
      message: 'This will reset all your progress. Do you want to continue?',
      confirmButtonText: 'Yes, Reset',
      cancelButtonText: 'Cancel',
      onConfirm: () => {
        setCurrentStep(1);
        setCompletedSteps([]);
        setStepSelections({
          1: [],
          2: [],
          3: [],
          4: [],
          5: []
        });
      }
    });
  };

  const handleRetry = () => {
    dispatch(fetchNcodes(id));
  };

  const showErrorAlert = () => {
    SweetAlertService.showError({
      title: 'Loading Error',
      message: `Error loading N Codes: ${ncodesError}`,
      confirmButtonText: 'Retry',
      onConfirm: handleRetry
    });
  };

  const currentConfig = stepConfigs[currentStep];

  const availableCodes = currentStep === 1 ? masterNCodes :
    currentStep === 4 ? stepSelections[3] :
      currentStep === 5 ? [] :
        (stepSelections[currentStep - 1] || []);

  // ============================================
  // CONDITIONAL RENDER LOGIC (Guard Clauses)
  // ============================================

  // ✅ 2. Loading State for Token Check
  if (tokenExpiryLoading) {
    return (
      <div className="w-full min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <ThreeDotBouncingLoader />
        </div>
      </div>
    );
  }


  // If tokenExpiryData exists and is not empty, the token is invalid/used.
  if (tokenExpiryData?.token_valid == false && tokenExpiryData?.expired == false) {
    return <InvalidTokenPage />;
  }
  // ✅ 4. Expired Token Logic
  if (tokenExpiryData?.expired == true) {
    return <ExpiredTokenPage />;
  }

  // 4. Existing Loading Logic for N Codes
  if (ncodesLoading) {
    return (
      <div className="w-full min-h-screen bg-white">
        <div className='mobile-responsive md-993:hidden'>
          <div className="">
            <img src="./../assets/images/Logo-Incredible-Leaders.png" width={120} alt="Incredible Leaders" />
          </div>
          {/* Mobile Menu Icon... */}
          <div className={`mobile-menu ${isMobileMenuOpen ? 'active' : ''}`} onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            {/* ... SVG Icon ... */}
            <svg width="24" height="19" viewBox="0 0 32 25" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0 1.52381C0 0.682225 0.68224 0 1.52381 0H30.4762C31.3178 0 32 0.682225 32 1.52381C32 2.36539 31.3178 3.04762 30.4762 3.04762H1.52381C0.68224 3.04762 0 2.36539 0 1.52381Z" fill="black" /><path d="M0 12.1902C0 11.3486 0.68224 10.6662 1.52381 10.6662L18.2857 10.6667C19.1273 10.6667 19.8095 11.3489 19.8095 12.1905C19.8095 13.0321 19.1273 13.7143 18.2857 13.7143L1.52381 13.714C0.68224 13.714 0 13.0316 0 12.1902Z" fill="black" /><path d="M1.52381 21.333C0.68224 21.333 0 22.0152 0 22.8568C0 23.6983 0.68224 24.3805 1.52381 24.3805H27.4286C28.2702 24.3805 28.9524 23.6983 28.9524 22.8568C28.9524 22.0152 28.2702 21.333 27.4286 21.333H1.52381Z" fill="black" /></svg>
          </div>
        </div>

        <div className="block md:flex lg:flex p-4">
          <Sidebar
            currentStep={currentStep}
            completedSteps={completedSteps}
            toolId={id}
            isMobileMenuOpen={isMobileMenuOpen}
            setIsMobileMenuOpen={setIsMobileMenuOpen}
          />
          <div className="flex-1 bg-white p-8 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF8E1C] mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading N Codes...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 5. Existing Error Logic
  if (ncodesError) {
    return (
      <div className="w-full min-h-screen bg-white">
        {/* Mobile Header logic same as above... */}
        <div className='mobile-responsive md-993:hidden'>
          <div className=""><img src="./../assets/images/Logo-Incredible-Leaders.png" width={120} alt="Incredible Leaders" /></div>
          <div className={`mobile-menu ${isMobileMenuOpen ? 'active' : ''}`} onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            <svg width="24" height="19" viewBox="0 0 32 25" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0 1.52381C0 0.682225 0.68224 0 1.52381 0H30.4762C31.3178 0 32 0.682225 32 1.52381C32 2.36539 31.3178 3.04762 30.4762 3.04762H1.52381C0.68224 3.04762 0 2.36539 0 1.52381Z" fill="black" /><path d="M0 12.1902C0 11.3486 0.68224 10.6662 1.52381 10.6662L18.2857 10.6667C19.1273 10.6667 19.8095 11.3489 19.8095 12.1905C19.8095 13.0321 19.1273 13.7143 18.2857 13.7143L1.52381 13.714C0.68224 13.714 0 13.0316 0 12.1902Z" fill="black" /><path d="M1.52381 21.333C0.68224 21.333 0 22.0152 0 22.8568C0 23.6983 0.68224 24.3805 1.52381 24.3805H27.4286C28.2702 24.3805 28.9524 23.6983 28.9524 22.8568C28.9524 22.0152 28.2702 21.333 27.4286 21.333H1.52381Z" fill="black" /></svg>
          </div>
        </div>

        <div className="block md:flex lg:flex p-4">
          <Sidebar
            currentStep={currentStep}
            completedSteps={completedSteps}
            toolId={id}
            isMobileMenuOpen={isMobileMenuOpen}
            setIsMobileMenuOpen={setIsMobileMenuOpen}
          />
          <div className="flex-1 bg-white p-8 flex items-center justify-center">
            <div className="text-center">
              <p className="text-red-600 mb-4">Error loading N Codes: {ncodesError}</p>
              <button
                onClick={showErrorAlert}
                className="px-4 py-2 bg-[#FF8E1C] text-white rounded hover:bg-[#ff7a00]"
              >
                Show Error Details
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 6. ID Missing Logic
  if (!id) {
    return (
      <div className="w-full min-h-screen bg-white">
        <div className="block md:flex lg:flex p-4">
          <div className="flex-1 bg-white p-8 flex items-center justify-center">
            <div className="text-center">
              <p className="text-gray-600">No tool ID provided in the URL</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 7. Main Render
  // If prospect params provided and not yet confirmed, show the prospect login form first
  if (prospectRequired && !prospectConfirmed) {
    const getHeaderTitle = () => {
      if (id === 'ncodes') return 'KNOW YOUR N CODES ASSESSMENT';
      if (id === 'ncodes-2') return 'YOUR N CODES ABOUT OTHERS ASSESSMENT';
      if (id === 'ncodes-3') return 'YOUR SALES N CODES ASSESSMENT';
      return 'KNOW YOUR N CODES ASSESSMENT';
    };

    const headerOverrides = {
      title: getHeaderTitle(),
      subtitle: 'Enter your details to access the tool',
      highlighted: ''
    };

    const initialValues = {
      company_id: Number(companyIdParam) || 0,
      company_name: companyNameParam || ''
    };

    return (
      <LoginFormFORprospect
        headerOverrides={headerOverrides}
        initialValues={initialValues}
        sessionType={sessionTypeParam}
        sessionName={sessionNameParam}
        companySessionId={companySessionIdParam}
        onSuccess={(payload) => {
          if (isSessionUrlFlow && payload?.userData) {
            setSessionUrlUserData(payload.userData);
          }
          // mark prospect confirmed and allow normal flow to continue
          setProspectConfirmed(true);
          // extract token from response payload (thunk returns data.data)
          const returnedToken = payload?.token || payload?.data?.token || null;
          if (returnedToken) setProspectToken(returnedToken);
          // fetch ncodes now that prospect is confirmed
          dispatch(fetchNcodes(id));
        }}
      />
    );
  }

  return (
    <div className="w-full min-h-screen bg-white">
      {/* Mobile Header - Shows below 993px */}
      <div className='mobile-responsive md-993:hidden'>
        <div className="">
          <img src="./../assets/images/Logo-Incredible-Leaders.png" width={120} alt="Incredible Leaders" />
        </div>
        <div
          className={`mobile-menu ${isMobileMenuOpen ? 'active' : ''}`}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          role="button"
          aria-label="Toggle menu"
          tabIndex={0}
        >
          <svg width="24" height="19" viewBox="0 0 32 25" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 1.52381C0 0.682225 0.68224 0 1.52381 0H30.4762C31.3178 0 32 0.682225 32 1.52381C32 2.36539 31.3178 3.04762 30.4762 3.04762H1.52381C0.68224 3.04762 0 2.36539 0 1.52381Z" fill="black" />
            <path d="M0 12.1902C0 11.3486 0.68224 10.6662 1.52381 10.6662L18.2857 10.6667C19.1273 10.6667 19.8095 11.3489 19.8095 12.1905C19.8095 13.0321 19.1273 13.7143 18.2857 13.7143L1.52381 13.714C0.68224 13.714 0 13.0316 0 12.1902Z" fill="black" />
            <path d="M1.52381 21.333C0.68224 21.333 0 22.0152 0 22.8568C0 23.6983 0.68224 24.3805 1.52381 24.3805H27.4286C28.2702 24.3805 28.9524 23.6983 28.9524 22.8568C28.9524 22.0152 28.2702 21.333 27.4286 21.333H1.52381Z" fill="black" />
          </svg>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="block md:flex lg:flex p-4">
        <Sidebar
          currentStep={currentStep}
          completedSteps={completedSteps}
          toolId={id}
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
        />
        <StepComponent
          stepNumber={currentStep}
          title={currentConfig.title}
          instruction={currentConfig.instruction}
          requiredCount={currentConfig.requiredCount}
          availableCodes={availableCodes}
          selectedCodes={stepSelections[currentStep]}
          onSelectionChange={handleSelectionChange}
          onNext={handleNext}
          onCancel={handleCancel}
          isLastStep={currentStep === 5}
          isReadOnly={currentConfig.isReadOnly}
          isResultStep={currentConfig.isResultStep}
          step4NCodes={stepSelections[4]} // Passed for display if needed
          resultCCodes={displayResultCodes}
          resultLoading={resultLoading}
          resultError={resultError}
        />
      </div>
    </div>
  );
};

export default Ncode;